// Weekday x hour grid shared by the statistics screens (courts and Beauty).
//
// Both responses carry a 168-cell `heat` array (weekday 0 = Sunday, hour 0..23) with the booked
// minutes and the minutes that were open for booking. The open-minutes field is called
// `availableMinutes` for courts and `agendaMinutes` for professionals, so the builders take its name.

import { weekdayCounts } from "./statsDates";

// Rows run Monday to Sunday; weekdays in the data are 0 = Sunday.
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const WEEKDAY_LONG = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const WEEKDAY_PLURAL = ["domingos", "lunes", "martes", "miércoles", "jueves", "viernes", "sábados"];
const DEFAULT_HOURS = Array.from({ length: 16 }, (_, i) => i + 8);

// Hours before 06:00 belong to the previous night's shift, so they sort after 23.
const hourSortKey = (h) => (h < 6 ? h + 24 : h);

/**
 * Hours (columns) to draw: the contiguous span covered by any open cell of any weekday, with
 * after-midnight hours placed after 23. Nothing open all week gets the usual 08-23.
 */
export const heatHours = (heat, availableKey = "availableMinutes") => {
  const keys = heat.filter((c) => c[availableKey] > 0).map((c) => hourSortKey(c.hour));
  if (keys.length === 0) return DEFAULT_HOURS;
  const first = Math.min(...keys);
  const last = Math.max(...keys);
  return Array.from({ length: last - first + 1 }, (_, i) => (first + i) % 24);
};

/**
 * Weekday x hour grid. `value` is booked / open (null = closed or blocked). `busyCourts` and
 * `openCourts` are the average number of resources (courts, or professionals for Beauty) that were
 * busy / open in that hour: the minutes averaged over how many times that weekday occurs in the
 * range, so the tooltip can say "2,8 de 4 ocupadas".
 */
export const buildHeatGrid = (info, availableKey = "availableMinutes") => {
  const hours = heatHours(info.heat, availableKey);
  const occurrences = weekdayCounts({ from: info.from, to: info.to });
  const cellAt = new Map(info.heat.map((c) => [`${c.weekday}-${c.hour}`, c]));
  const rows = WEEKDAY_ORDER.map((weekday) => ({
    weekday,
    label: WEEKDAY_SHORT[weekday],
    cells: hours.map((hour) => {
      const cell = cellAt.get(`${weekday}-${hour}`);
      const open = cell && cell[availableKey] > 0;
      const days = occurrences[weekday] || 1;
      return {
        hour,
        value: open ? cell.bookedMinutes / cell[availableKey] : null,
        busyCourts: open ? cell.bookedMinutes / 60 / days : null,
        openCourts: open ? cell[availableKey] / 60 / days : null,
        days,
      };
    }),
  }));
  return { hours, rows };
};

export const weekdayLong = (weekday) => WEEKDAY_LONG[weekday];
export const weekdayPlural = (weekday) => WEEKDAY_PLURAL[weekday];

/** Peak, quietest and "quiet slot" count of a grid; `null` peak/low when no cell is open. */
export const heatExtremes = (grid, lowThreshold) => {
  let peak = null;
  let low = null;
  let lowCount = 0;
  grid.rows.forEach((row) =>
    row.cells.forEach((cell) => {
      if (cell.value === null) return;
      const found = { weekday: row.weekday, hour: cell.hour, value: cell.value };
      if (!peak || cell.value > peak.value) peak = found;
      if (!low || cell.value < low.value) low = found;
      if (cell.value < lowThreshold) lowCount += 1;
    })
  );
  return { peak, low, lowCount };
};
